/**
 * Theme packs.
 *
 * Cycle 1's clearest finding: her attention is content-gated. A counting game
 * about "objects" and the same game about dinosaurs are not the same activity.
 * Every default config therefore pulls its nouns, pictures and story framing
 * from the pack matching the child's live Interest.
 */

export interface ThemeItem {
  id: string
  label: string
  emoji: string
  /** Natural category — the first sorting criterion and a deduction attribute. */
  group: string
  /** Where it belongs — a deduction attribute. */
  home: string
  /** The second sorting criterion, so a two-attribute hunt is always possible. */
  size: 'big' | 'small'
}

export interface ThemeChoice {
  id: string
  label: string
  emoji: string
}

export interface ThemeSequence {
  title: string
  /** Seven steps, sliced by difficulty. Ordered as written. */
  steps: ThemeChoice[]
}

export interface ThemePack {
  key: string
  label: string
  /** Matching aliases, all lowercase. */
  aliases: readonly string[]
  /** Singular noun for the counting game, e.g. "dinosaur". */
  objectNoun: string
  objectEmoji: string
  /** At least six, so sorting/memory/deduction have room. */
  items: readonly ThemeItem[]
  /** Sorting is framed as a hunt with a job to do — never an abstract sort. */
  huntScenario: 'treasure_hunt' | 'animal_clinic' | 'rescue_mission' | 'grocery_run' | 'custom'
  huntPrompt: string
  huntVerb: string
  binNoun: string
  characters: readonly ThemeChoice[]
  settings: readonly ThemeChoice[]
  problems: readonly ThemeChoice[]
  resolutions: readonly ThemeChoice[]
  sequence: ThemeSequence
  /** Who she reads to. Her caregiving instinct is a motivation engine. */
  audience: string
}

const EVERYDAY: ThemePack = {
  key: 'everyday',
  label: 'Everyday Things',
  aliases: ['default', 'everyday', 'home', 'things'],
  objectNoun: 'button',
  objectEmoji: '🔘',
  items: [
    { id: 'apple', label: 'apple', emoji: '🍎', group: 'food', home: 'kitchen', size: 'small' },
    { id: 'sock', label: 'sock', emoji: '🧦', group: 'clothes', home: 'drawer', size: 'small' },
    { id: 'melon', label: 'melon', emoji: '🍉', group: 'food', home: 'kitchen', size: 'big' },
    { id: 'coat', label: 'coat', emoji: '🧥', group: 'clothes', home: 'hall', size: 'big' },
    { id: 'bread', label: 'bread', emoji: '🍞', group: 'food', home: 'cupboard', size: 'big' },
    { id: 'hat', label: 'hat', emoji: '🧢', group: 'clothes', home: 'shelf', size: 'small' },
  ],
  huntScenario: 'treasure_hunt',
  huntPrompt: 'The toy box tipped over! Hunt for each thing and put it where it lives.',
  huntVerb: 'find',
  binNoun: 'basket',
  characters: [
    { id: 'girl', label: 'a brave girl', emoji: '👧' },
    { id: 'puppy', label: 'a puppy', emoji: '🐶' },
    { id: 'robot', label: 'a small robot', emoji: '🤖' },
  ],
  settings: [
    { id: 'house', label: 'a cozy house', emoji: '🏠' },
    { id: 'park', label: 'the park', emoji: '🌳' },
    { id: 'beach', label: 'the beach', emoji: '🏖️' },
  ],
  problems: [
    { id: 'lost', label: 'something got lost', emoji: '❓' },
    { id: 'stuck', label: 'someone got stuck', emoji: '🪤' },
    { id: 'rain', label: 'it started to rain', emoji: '🌧️' },
  ],
  resolutions: [
    { id: 'help', label: 'a friend helped', emoji: '🤝' },
    { id: 'idea', label: 'she had a clever idea', emoji: '💡' },
    { id: 'share', label: 'everybody shared', emoji: '🎁' },
  ],
  sequence: {
    title: 'Making a sandwich',
    steps: [
      { id: 'wash', label: 'wash hands', emoji: '🧼' },
      { id: 'bread', label: 'get the bread', emoji: '🍞' },
      { id: 'spread', label: 'spread the butter', emoji: '🧈' },
      { id: 'fill', label: 'add the cheese', emoji: '🧀' },
      { id: 'top', label: 'put the top slice on', emoji: '🥪' },
      { id: 'cut', label: 'cut it in half', emoji: '🔪' },
      { id: 'eat', label: 'eat it up', emoji: '😋' },
    ],
  },
  audience: 'Teddy',
}

const DINOSAURS: ThemePack = {
  key: 'dinosaurs',
  label: 'Dinosaurs',
  aliases: ['dinosaur', 'dinosaurs', 'dino', 'dinos', 'fossils'],
  objectNoun: 'dinosaur',
  objectEmoji: '🦕',
  items: [
    { id: 'trex', label: 'T. rex', emoji: '🦖', group: 'meat eater', home: 'forest', size: 'big' },
    { id: 'bronto', label: 'brontosaurus', emoji: '🦕', group: 'plant eater', home: 'swamp', size: 'big' },
    { id: 'raptor', label: 'raptor', emoji: '🐊', group: 'meat eater', home: 'desert', size: 'small' },
    { id: 'trike', label: 'triceratops', emoji: '🦏', group: 'plant eater', home: 'forest', size: 'big' },
    { id: 'ptero', label: 'pterodactyl', emoji: '🦅', group: 'meat eater', home: 'cliff', size: 'small' },
    { id: 'stego', label: 'stegosaurus', emoji: '🐲', group: 'plant eater', home: 'desert', size: 'small' },
  ],
  huntScenario: 'animal_clinic',
  huntPrompt: 'The dinosaur clinic is open! Every dinosaur needs the right room. Who goes where?',
  huntVerb: 'rescue',
  binNoun: 'room',
  characters: [
    { id: 'baby-dino', label: 'a baby dinosaur', emoji: '🥚' },
    { id: 'trex', label: 'a friendly T. rex', emoji: '🦖' },
    { id: 'ranger', label: 'a dinosaur ranger', emoji: '🧑‍🌾' },
  ],
  settings: [
    { id: 'jungle', label: 'the steamy jungle', emoji: '🌴' },
    { id: 'volcano', label: 'near the volcano', emoji: '🌋' },
    { id: 'lake', label: 'the big lake', emoji: '🏞️' },
  ],
  problems: [
    { id: 'lost-egg', label: 'an egg rolled away', emoji: '🥚' },
    { id: 'hungry', label: 'everyone got hungry', emoji: '🍃' },
    { id: 'storm', label: 'a storm rolled in', emoji: '⛈️' },
  ],
  resolutions: [
    { id: 'rolled-back', label: 'they rolled the egg home', emoji: '🏡' },
    { id: 'found-food', label: 'they found a leafy tree', emoji: '🌳' },
    { id: 'cave', label: 'they hid in a warm cave', emoji: '🕳️' },
  ],
  sequence: {
    title: 'A dinosaur hatches',
    steps: [
      { id: 'nest', label: 'the mother builds a nest', emoji: '🪹' },
      { id: 'egg', label: 'she lays an egg', emoji: '🥚' },
      { id: 'warm', label: 'the egg stays warm', emoji: '☀️' },
      { id: 'crack', label: 'the shell cracks', emoji: '💥' },
      { id: 'hatch', label: 'the baby hatches', emoji: '🐣' },
      { id: 'eat', label: 'it eats its first leaves', emoji: '🌿' },
      { id: 'grow', label: 'it grows enormous', emoji: '🦕' },
    ],
  },
  audience: 'her toy dinosaur',
}

const CATS: ThemePack = {
  key: 'cats',
  label: 'Cats',
  aliases: ['cat', 'cats', 'kitten', 'kittens', 'kitty'],
  objectNoun: 'kitten',
  objectEmoji: '🐱',
  items: [
    { id: 'tabby', label: 'tabby cat', emoji: '🐈', group: 'cat', home: 'sofa', size: 'big' },
    { id: 'mouse', label: 'mouse', emoji: '🐭', group: 'friend', home: 'wall', size: 'small' },
    { id: 'kitten', label: 'kitten', emoji: '🐱', group: 'cat', home: 'basket', size: 'small' },
    { id: 'dog', label: 'dog', emoji: '🐶', group: 'friend', home: 'garden', size: 'big' },
    { id: 'black', label: 'black cat', emoji: '🐈‍⬛', group: 'cat', home: 'window', size: 'big' },
    { id: 'bird', label: 'bird', emoji: '🐦', group: 'friend', home: 'window', size: 'small' },
  ],
  huntScenario: 'animal_clinic',
  huntPrompt: 'You are the cat doctor. Each visitor needs the right bed — go and get them!',
  huntVerb: 'carry',
  binNoun: 'bed',
  characters: [
    { id: 'kitten', label: 'a curious kitten', emoji: '🐱' },
    { id: 'old-cat', label: 'a sleepy old cat', emoji: '🐈' },
    { id: 'vet', label: 'a cat doctor', emoji: '👩‍⚕️' },
  ],
  settings: [
    { id: 'roof', label: 'the rooftops', emoji: '🏘️' },
    { id: 'kitchen', label: 'a warm kitchen', emoji: '🍳' },
    { id: 'garden', label: 'the night garden', emoji: '🌙' },
  ],
  problems: [
    { id: 'stuck-tree', label: 'the kitten got stuck up a tree', emoji: '🌳' },
    { id: 'no-dinner', label: 'the bowl was empty', emoji: '🥣' },
    { id: 'scared', label: 'a loud noise scared everyone', emoji: '📢' },
  ],
  resolutions: [
    { id: 'ladder', label: 'a friend brought a ladder', emoji: '🪜' },
    { id: 'fed', label: 'they filled the bowl together', emoji: '🐟' },
    { id: 'cuddle', label: 'they curled up together', emoji: '💤' },
  ],
  sequence: {
    title: 'Taking care of a kitten',
    steps: [
      { id: 'wake', label: 'the kitten wakes up', emoji: '🌅' },
      { id: 'stretch', label: 'it has a big stretch', emoji: '🙆' },
      { id: 'food', label: 'you fill the bowl', emoji: '🥣' },
      { id: 'eat', label: 'the kitten eats', emoji: '😻' },
      { id: 'play', label: 'you play with the string', emoji: '🧶' },
      { id: 'brush', label: 'you brush its fur', emoji: '🪮' },
      { id: 'nap', label: 'it curls up for a nap', emoji: '💤' },
    ],
  },
  audience: 'the kitten',
}

const SPACE: ThemePack = {
  key: 'space',
  label: 'Space',
  aliases: ['space', 'planets', 'solar system', 'rockets', 'rocket', 'stars', 'moon'],
  objectNoun: 'star',
  objectEmoji: '⭐',
  items: [
    { id: 'sun', label: 'the Sun', emoji: '☀️', group: 'space rock', home: 'the centre', size: 'big' },
    { id: 'rocket', label: 'rocket', emoji: '🚀', group: 'machine', home: 'the launchpad', size: 'big' },
    { id: 'moon', label: 'the Moon', emoji: '🌙', group: 'space rock', home: 'orbit', size: 'small' },
    { id: 'rover', label: 'rover', emoji: '🛻', group: 'machine', home: 'the launchpad', size: 'small' },
    { id: 'earth', label: 'Earth', emoji: '🌍', group: 'space rock', home: 'orbit', size: 'big' },
    { id: 'mars', label: 'Mars', emoji: '🔴', group: 'space rock', home: 'the far orbit', size: 'big' },
  ],
  huntScenario: 'rescue_mission',
  huntPrompt: 'Mission control needs you! Collect each thing floating loose and dock it in the right bay.',
  huntVerb: 'collect',
  binNoun: 'bay',
  characters: [
    { id: 'astronaut', label: 'a small astronaut', emoji: '👩‍🚀' },
    { id: 'alien', label: 'a friendly alien', emoji: '👽' },
    { id: 'rover', label: 'a brave rover', emoji: '🛻' },
  ],
  settings: [
    { id: 'moon', label: 'the dusty Moon', emoji: '🌕' },
    { id: 'station', label: 'the space station', emoji: '🛰️' },
    { id: 'mars', label: 'red Mars', emoji: '🔴' },
  ],
  problems: [
    { id: 'no-fuel', label: 'the rocket ran out of fuel', emoji: '⛽' },
    { id: 'lost-tool', label: 'a tool floated away', emoji: '🔧' },
    { id: 'dark', label: 'the lights went out', emoji: '🌑' },
  ],
  resolutions: [
    { id: 'towed', label: 'a friend towed them home', emoji: '🪢' },
    { id: 'caught', label: 'they caught it with the net', emoji: '🥅' },
    { id: 'starlight', label: 'the stars lit the way', emoji: '✨' },
  ],
  sequence: {
    title: 'Launching a rocket',
    steps: [
      { id: 'build', label: 'build the rocket', emoji: '🔧' },
      { id: 'fuel', label: 'fill it with fuel', emoji: '⛽' },
      { id: 'suit', label: 'put on the space suit', emoji: '👩‍🚀' },
      { id: 'climb', label: 'climb inside', emoji: '🪜' },
      { id: 'count', label: 'count down from ten', emoji: '🔟' },
      { id: 'launch', label: 'lift off', emoji: '🚀' },
      { id: 'orbit', label: 'float in orbit', emoji: '🛰️' },
    ],
  },
  audience: 'her astronaut doll',
}

const OCEAN: ThemePack = {
  key: 'ocean',
  label: 'The Ocean',
  aliases: ['ocean', 'sea', 'underwater', 'fish', 'whales', 'mermaid'],
  objectNoun: 'shell',
  objectEmoji: '🐚',
  items: [
    { id: 'whale', label: 'whale', emoji: '🐋', group: 'swimmer', home: 'deep water', size: 'big' },
    { id: 'crab', label: 'crab', emoji: '🦀', group: 'walker', home: 'the sand', size: 'small' },
    { id: 'seahorse', label: 'seahorse', emoji: '🐴', group: 'swimmer', home: 'the seaweed', size: 'small' },
    { id: 'turtle', label: 'turtle', emoji: '🐢', group: 'walker', home: 'the sand', size: 'big' },
    { id: 'octopus', label: 'octopus', emoji: '🐙', group: 'swimmer', home: 'a cave', size: 'big' },
    { id: 'starfish', label: 'starfish', emoji: '⭐', group: 'walker', home: 'a rock pool', size: 'small' },
  ],
  huntScenario: 'rescue_mission',
  huntPrompt: 'The tide went out and everyone is stranded! Scoop each one up and take it home.',
  huntVerb: 'rescue',
  binNoun: 'pool',
  characters: [
    { id: 'turtle', label: 'a little turtle', emoji: '🐢' },
    { id: 'diver', label: 'a diver', emoji: '🤿' },
    { id: 'octopus', label: 'a helpful octopus', emoji: '🐙' },
  ],
  settings: [
    { id: 'reef', label: 'the coral reef', emoji: '🪸' },
    { id: 'wreck', label: 'an old shipwreck', emoji: '🚢' },
    { id: 'pool', label: 'a rock pool', emoji: '🪨' },
  ],
  problems: [
    { id: 'net', label: 'someone got tangled in a net', emoji: '🥅' },
    { id: 'lost-way', label: 'they lost the way home', emoji: '🧭' },
    { id: 'storm', label: 'a big wave came', emoji: '🌊' },
  ],
  resolutions: [
    { id: 'cut-free', label: 'the octopus untangled it', emoji: '🐙' },
    { id: 'followed', label: 'they followed the bubbles home', emoji: '🫧' },
    { id: 'held-on', label: 'they held on tight together', emoji: '🤝' },
  ],
  sequence: {
    title: 'A turtle goes to sea',
    steps: [
      { id: 'nest', label: 'the egg is buried in sand', emoji: '🏝️' },
      { id: 'crack', label: 'the shell cracks', emoji: '🥚' },
      { id: 'dig', label: 'the baby digs upward', emoji: '⬆️' },
      { id: 'moon', label: 'it follows the moonlight', emoji: '🌕' },
      { id: 'crawl', label: 'it crawls to the water', emoji: '🐢' },
      { id: 'swim', label: 'it swims out to sea', emoji: '🌊' },
      { id: 'grow', label: 'it grows big and strong', emoji: '🐢' },
    ],
  },
  audience: 'her toy whale',
}

const GARDEN: ThemePack = {
  key: 'garden',
  label: 'The Garden',
  aliases: ['garden', 'plants', 'flowers', 'seeds', 'bugs', 'nature'],
  objectNoun: 'seed',
  objectEmoji: '🌱',
  items: [
    { id: 'bee', label: 'bee', emoji: '🐝', group: 'bug', home: 'the hive', size: 'small' },
    { id: 'sunflower', label: 'sunflower', emoji: '🌻', group: 'plant', home: 'the soil', size: 'big' },
    { id: 'butterfly', label: 'butterfly', emoji: '🦋', group: 'bug', home: 'a flower', size: 'big' },
    { id: 'tulip', label: 'tulip', emoji: '🌷', group: 'plant', home: 'the soil', size: 'small' },
    { id: 'ant', label: 'ant', emoji: '🐜', group: 'bug', home: 'the nest', size: 'small' },
    { id: 'mushroom', label: 'mushroom', emoji: '🍄', group: 'plant', home: 'the shade', size: 'small' },
  ],
  huntScenario: 'treasure_hunt',
  huntPrompt: 'Grab the magnifier — go hunting in the garden and bring each find to the right jar.',
  huntVerb: 'hunt for',
  binNoun: 'jar',
  characters: [
    { id: 'ladybug', label: 'a tiny ladybug', emoji: '🐞' },
    { id: 'gardener', label: 'a young gardener', emoji: '👧' },
    { id: 'worm', label: 'a wiggly worm', emoji: '🪱' },
  ],
  settings: [
    { id: 'patch', label: 'the vegetable patch', emoji: '🥕' },
    { id: 'greenhouse', label: 'the greenhouse', emoji: '🏡' },
    { id: 'pond', label: 'the pond', emoji: '🐸' },
  ],
  problems: [
    { id: 'dry', label: 'the soil went dry', emoji: '🏜️' },
    { id: 'wind', label: 'the wind blew the seeds away', emoji: '💨' },
    { id: 'snail', label: 'a snail ate the leaves', emoji: '🐌' },
  ],
  resolutions: [
    { id: 'watered', label: 'they watered every plant', emoji: '💧' },
    { id: 'replanted', label: 'they planted the seeds again', emoji: '🌱' },
    { id: 'fence', label: 'they made a little fence', emoji: '🪵' },
  ],
  sequence: {
    title: 'Growing a sunflower',
    steps: [
      { id: 'seed', label: 'hold the seed', emoji: '🌰' },
      { id: 'dig', label: 'dig a small hole', emoji: '🕳️' },
      { id: 'plant', label: 'drop the seed in', emoji: '🌱' },
      { id: 'water', label: 'water the soil', emoji: '💧' },
      { id: 'sprout', label: 'a sprout pushes up', emoji: '🌿' },
      { id: 'grow', label: 'the stem grows tall', emoji: '📏' },
      { id: 'bloom', label: 'the flower opens', emoji: '🌻' },
    ],
  },
  audience: 'her doll',
}

export const THEME_PACKS: readonly ThemePack[] = [EVERYDAY, DINOSAURS, CATS, SPACE, OCEAN, GARDEN]

export const DEFAULT_THEME_PACK = EVERYDAY

/**
 * Match a free-text interest topic to a pack. Unknown topics fall back to the
 * everyday pack — the spec still carries the original topic string as `theme`,
 * so the renderer can show her word even when the pack is generic.
 */
export function resolveThemePack(theme?: string): ThemePack {
  if (!theme) return DEFAULT_THEME_PACK
  const needle = theme.trim().toLowerCase()
  if (!needle) return DEFAULT_THEME_PACK
  for (const pack of THEME_PACKS) {
    if (pack.key === needle) return pack
    if (pack.aliases.some((alias) => alias === needle)) return pack
  }
  for (const pack of THEME_PACKS) {
    if (pack.aliases.some((alias) => needle.includes(alias))) return pack
  }
  return DEFAULT_THEME_PACK
}
